/**
 * Exports générés dans le navigateur à partir des données complètes renvoyées
 * par GET /reports/export?format=json (aucun plafond d'affichage).
 * Le CSV et le GeoJSON sont produits directement par le serveur.
 */
import jsPDF from 'jspdf';
import * as shpwrite from '@mapbox/shp-write';
import { REPORT_STATUS_LABELS_FR, PRIORITY_LEVEL_LABELS_FR } from '@ci-ville/shared';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
// ── Rapport PDF (liste) ───────────────────────────────────────────
export function exportReportsListPDF(reports, tenantName, filtersLabel) {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const now = format(new Date(), 'PPPP', { locale: fr });
    pdf.setFillColor(33, 150, 243);
    pdf.rect(0, 0, pageWidth, 24, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('CI-VILLE', 12, 15);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(tenantName, 12, 20);
    pdf.text(`Rapport généré le ${now}`, pageWidth - 12, 15, { align: 'right' });
    pdf.text(`${reports.length} signalement(s)`, pageWidth - 12, 20, { align: 'right' });
    pdf.setTextColor(20, 20, 20);
    let y = 32;
    if (filtersLabel) {
        pdf.setFontSize(8);
        pdf.setTextColor(100, 110, 125);
        pdf.text(`Filtres : ${filtersLabel}`, 10, 29);
        pdf.setTextColor(20, 20, 20);
        y = 35;
    }
    const cols = [
        { label: 'Référence', w: 30 },
        { label: 'Titre', w: 62 },
        { label: 'Catégorie', w: 40 },
        { label: 'Priorité', w: 20 },
        { label: 'Statut', w: 22 },
        { label: 'Créé le', w: 22 },
        { label: 'Assigné à', w: 32 },
        { label: 'Adresse', w: 49 },
    ];
    const startX = 10;
    const drawHeader = (yPos) => {
        pdf.setFillColor(240, 244, 250);
        pdf.rect(startX, yPos - 5, pageWidth - 20, 7, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        let x = startX + 2;
        cols.forEach((c) => {
            pdf.text(c.label, x, yPos);
            x += c.w;
        });
    };
    drawHeader(y);
    y += 6;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    reports.forEach((r) => {
        if (y > pageHeight - 15) {
            pdf.addPage();
            y = 12;
            drawHeader(y);
            y += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
        }
        const row = [
            r.referenceNumber,
            (r.title ?? '').substring(0, 44),
            (r.category ?? 'Sans catégorie').substring(0, 28),
            PRIORITY_LEVEL_LABELS_FR[r.priorityLevel] ?? r.priorityLevel,
            REPORT_STATUS_LABELS_FR[r.status] ?? r.status,
            format(new Date(r.createdAt), 'yyyy-MM-dd'),
            (r.assignedTo ?? 'Non assigné').substring(0, 22),
            (r.address ?? '').substring(0, 36),
        ];
        let x = startX + 2;
        cols.forEach((c, i) => {
            pdf.text(String(row[i] ?? ''), x, y);
            x += c.w;
        });
        y += 5;
        pdf.setDrawColor(230, 234, 240);
        pdf.line(startX, y - 2, pageWidth - 10, y - 2);
    });
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
        pdf.text('CI-VILLE par GEOSIRA INC. Données hébergées au Québec.', 10, pageHeight - 5);
    }
    pdf.save(`rapport-${tenantName.toLowerCase().replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
}
// ── Export Shapefile SIG ──────────────────────────────────────────
export async function exportReportsShapefile(reports) {
    const geojson = {
        type: 'FeatureCollection',
        features: reports
            .filter((r) => r.latitude != null && r.longitude != null)
            .map((r) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [r.longitude, r.latitude] },
            // Noms de champs DBF limités à 10 caractères
            properties: {
                ref: r.referenceNumber,
                titre: (r.title ?? '').substring(0, 250),
                categorie: (r.category ?? '').substring(0, 250),
                types: (r.types ?? '').substring(0, 250),
                statut: r.status,
                priorite: r.priorityLevel,
                score: r.priorityScore ?? 0,
                cree_le: r.createdAt,
                resolu_le: r.resolvedAt ?? '',
                sla_depass: r.slaBreached ? 1 : 0,
                adresse: (r.address ?? '').substring(0, 250),
            },
        })),
    };
    const name = `signalements-${format(new Date(), 'yyyy-MM-dd')}`;
    // shp-write produit du base64 par défaut, que son propre download() traite à tort comme une URL
    const blob = (await shpwrite.zip(geojson, {
        folder: name,
        outputType: 'blob',
        compression: 'DEFLATE',
        types: { point: 'signalements', polygon: 'signalements_polygons', polyline: 'signalements_lines' },
    }));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.zip`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
//# sourceMappingURL=exports.js.map